/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

// Package Imports
import React, { useId, useState, useMemo, useRef, useSyncExternalStore } from 'react';
import Select from 'react-select';
import classNames from 'classnames';

// Styles
import baseStyles from './stylizedSelect.module.scss';

// State name to abbreviation mapping for autofill compatibility
const STATE_NAME_TO_ABBR: Record<string, string> = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
  colorado: 'CO', connecticut: 'CT', delaware: 'DE', florida: 'FL', georgia: 'GA',
  hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA',
  kansas: 'KS', kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
  massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS', missouri: 'MO',
  montana: 'MT', nebraska: 'NE', nevada: 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
  'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH',
  oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT',
  virginia: 'VA', washington: 'WA', 'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY',
  'district of columbia': 'DC',
};

const ABBR_TO_STATE_NAME: Record<string, string> = Object.entries(STATE_NAME_TO_ABBR).reduce(
  (acc, [stateName, abbr]) => {
    acc[abbr] = stateName;
    return acc;
  },
  {} as Record<string, string>,
);

const subscribeToClient = (onChange: () => void) => {
  onChange();
  return () => {};
};

const getClientSnapshot = () => true;
const getServerSnapshot = () => false;

type Option = {
  value: string | number;
  label: string;
};

type StyleProps = {
  label?: string;
  selectWrapper?: string;
  container?: string;
  control?: string;
  menu?: string;
  option?: string;
  isSelected?: string;
  isFocused?: string;
  indicatorSeparator?: string;
};

type StylizedSelectProps = {
  label?: string;
  hiddenLabel?: string;
  name?: string;
  placeHolder?: string;
  options: Option[];
  onChange: (value: string | number) => void;
  defaultValue?: any;
  styles?: StyleProps;
  disabled?: boolean;
  required?: boolean;
  variantLabel?: string;
  size?: 'small' | 'large';
  classname?: string;
  ariaLabelledBy?: string;
};

const getDefaultValueAsValue = (defaultValue: any, options: Option[]) => {
  if (defaultValue === null || defaultValue === undefined) return undefined;

  let value;
  if (typeof defaultValue === 'object' && 'value' in defaultValue) {
    value = defaultValue.value;
  } else {
    value = defaultValue;
  }

  if (typeof value === 'string') {
    const lowerValue = value.toLowerCase().trim();
    if (STATE_NAME_TO_ABBR[lowerValue]) {
      return STATE_NAME_TO_ABBR[lowerValue];
    }

    const upperValue = value.toUpperCase().trim();
    if (upperValue.length === 2 && options.some((option) => option.value === upperValue)) {
      return upperValue;
    }
  }

  return value;
};

export default function StylizedSelect({
  label,
  hiddenLabel,
  name = '',
  placeHolder = '',
  options,
  onChange,
  defaultValue,
  styles,
  variantLabel,
  size = 'large',
  classname,
  disabled = false,
  required = false,
  ariaLabelledBy,
}: StylizedSelectProps) {
  const actualDefaultValue = getDefaultValueAsValue(defaultValue, options);
  const isClient = useSyncExternalStore(subscribeToClient, getClientSnapshot, getServerSnapshot);
  const [selectedValue, setSelectedValue] = useState<string | number | undefined>(actualDefaultValue);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const inputId = useId();
  const labelId = `${inputId}-label`;
  const instanceId = name || inputId.replace(/:/g, '');
  const selectRef = useRef<any>(null);

  const currentSelectedValue = useMemo(() => {
    if (hasUserInteracted && selectedValue !== undefined) {
      return selectedValue;
    }
    return actualDefaultValue ?? selectedValue;
  }, [selectedValue, actualDefaultValue, hasUserInteracted]);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === currentSelectedValue) || null,
    [options, currentSelectedValue],
  );

  const handleChange = (option: any) => {
    setHasUserInteracted(true);
    setSelectedValue(option?.value);
    onChange(option?.value);
  };

  const handleInputChange = (inputValue: string, actionMeta: any) => {
    if (actionMeta.action !== 'input-change') return;

    const input = inputValue.trim().toLowerCase();
    if (!input) return;

    const exactAbbrMatch = options.find(
      (option) => String(option.value).toLowerCase() === input,
    );

    if (exactAbbrMatch) {
      setTimeout(() => {
        if (selectRef.current) {
          selectRef.current.setValue(exactAbbrMatch, 'select-option');
          selectRef.current.blur();
        }
        setHasUserInteracted(true);
        setSelectedValue(exactAbbrMatch.value);
        onChange(exactAbbrMatch.value);
      }, 0);
      return;
    }

    const stateAbbr = STATE_NAME_TO_ABBR[input];
    if (stateAbbr) {
      const stateOption = options.find((option) => option.value === stateAbbr);

      if (stateOption) {
        setTimeout(() => {
          if (selectRef.current) {
            selectRef.current.setValue(stateOption, 'select-option');
            selectRef.current.blur();
          }
          setHasUserInteracted(true);
          setSelectedValue(stateOption.value);
          onChange(stateOption.value);
        }, 0);
      }
    }
  };

  const customFilterOption = (option: any, rawInput: string) => {
    if (!rawInput) return true;

    const input = rawInput.toLowerCase();
    const value = String(option.value).toLowerCase();
    const optionLabel = String(option.label).toLowerCase();

    if (value.includes(input) || optionLabel.includes(input)) {
      return true;
    }

    const fullStateName = ABBR_TO_STATE_NAME[String(option.value).toUpperCase()];
    if (fullStateName && fullStateName.toLowerCase().includes(input)) {
      return true;
    }

    return false;
  };

  const serverDisplayLabel = selectedOption?.label ?? placeHolder;

  return (
    <div
      className={classNames(
        baseStyles?.selectWrapper,
        styles?.selectWrapper,
        size === 'small' ? baseStyles?.small : baseStyles?.large,
        disabled && baseStyles?.disabled,
        classname && baseStyles?.[classname],
      )}
    >
      {label && (
        <label
          id={labelId}
          htmlFor={inputId}
          className={`${baseStyles?.label} ${styles?.label || ''} ${required ? baseStyles.required : ''} ${variantLabel === 'inline' ? baseStyles.inline : ''}`}
        >
          {label}
        </label>
      )}
      {hiddenLabel && (
        <label id={`${inputId}-label`} className="sr-only">
          {hiddenLabel}
        </label>
      )}
      <input
        id={inputId}
        aria-labelledby={ariaLabelledBy || labelId}
        style={{ display: 'none' }}
      />
      {isClient ? (
        <Select
          key={`select-${JSON.stringify(defaultValue)}`}
          ref={selectRef}
          instanceId={instanceId}
          inputId={inputId}
          placeholder={placeHolder}
          aria-labelledby={`${inputId}-label`}
          value={selectedOption}
          options={options}
          name={name}
          filterOption={customFilterOption}
          onInputChange={handleInputChange}
          styles={{
            placeholder: (base) => ({
              ...base,
              color: '#767676',
            }),
          }}
          classNames={{
            container: () => `${baseStyles?.container} ${styles?.container || ''}`,
            control: (state) =>
              state.isFocused
                ? `${baseStyles?.control} ${baseStyles?.isFocused} ${styles?.control || ''} ${styles?.isFocused || ''}`
                : `${baseStyles?.control} ${styles?.control || ''} ${disabled ? baseStyles.disabled : ''}`,
            menu: () => `${baseStyles?.menu} ${styles?.menu || ''}`,
            option: (state) => {
              if (state.isFocused) {
                return `${baseStyles?.option} ${baseStyles?.isFocused} ${styles?.option || ''} ${styles?.isFocused || ''}`;
              }
              if (state.isSelected) {
                return `${baseStyles?.option} ${baseStyles?.isSelected} ${styles?.option || ''} ${styles?.isSelected || ''}`;
              }
              return `${baseStyles?.option} ${styles?.option || ''}`;
            },
            indicatorSeparator: () => `${baseStyles?.indicatorSeparator} ${styles?.indicatorSeparator || ''}`,
          }}
          onChange={handleChange}
          isDisabled={disabled}
        />
      ) : (
        <div
          className={classNames(
            baseStyles?.control,
            styles?.control,
            disabled && baseStyles.disabled,
          )}
          aria-hidden="true"
        >
          {serverDisplayLabel}
        </div>
      )}
    </div>
  );
}
